// useRealtimeConsultation.ts
// React hook combining SentenceBuffer + RealtimeApiService

import { useState, useEffect, useRef, useCallback } from 'react';
import { SentenceBuffer, BufferedSentence, TranscriptSegment } from '@/utils/sentenceBuffer';
import { realtimeApi, ChunkResult, FinalResult } from '@/services/realtimeApi';

// Config interface
export interface RealtimeConsultationConfig {
  sessionId: string;                              // Required
  minWordsBeforeFlush?: number;                   // Default: 10
  maxWordsBeforeForceFlush?: number;              // Default: 60
  enableRealTimeSOAP?: boolean;                   // Default: true
  onSentenceSent?: (sentence: BufferedSentence) => void;
  onDeltaReceived?: (delta: ChunkResult) => void;
  onError?: (error: Error) => void;
}

// Transcript entry for display
export interface TranscriptEntry {
  speaker: string;
  text: string;
  timestamp: number;
}

// Accumulated SOAP data
export interface AccumulatedSOAP {
  subjective: string[];
  objective: string[];
  plan: string[];
}

// State interface
export interface RealtimeConsultationState {
  isStreaming: boolean;           // Currently processing transcripts
  isPaused: boolean;              // Paused by user
  transcript: TranscriptEntry[];  // Array of { speaker, text, timestamp }
  currentPartial: string;         // Live interim text (not yet final)
  accumulatedSOAP: AccumulatedSOAP;
  pendingChunks: number;          // Chunks sent but not yet responded
  isOnline: boolean;              // Network status
  error: string | null;           // Last error message
}

// Actions interface
export interface RealtimeConsultationActions {
  addTranscriptSegment: (text: string, speaker: string, isFinal: boolean) => void;
  pause: () => void;
  resume: () => void;
  finish: () => Promise<FinalResult>;
  reset: () => void;
  flushBuffer: () => void;
}

const DEFAULT_SOAP: AccumulatedSOAP = {
  subjective: [],
  objective: [],
  plan: [],
};

export function useRealtimeConsultation(
  config: RealtimeConsultationConfig
): [RealtimeConsultationState, RealtimeConsultationActions] {
  const {
    sessionId,
    minWordsBeforeFlush = 10,
    maxWordsBeforeForceFlush = 60,
    enableRealTimeSOAP = true,
    onSentenceSent,
    onDeltaReceived,
    onError,
  } = config;

  // State
  const [isStreaming, setIsStreaming] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [currentPartial, setCurrentPartial] = useState('');
  const [accumulatedSOAP, setAccumulatedSOAP] = useState<AccumulatedSOAP>(DEFAULT_SOAP);
  const [pendingChunks, setPendingChunks] = useState(0);
  const [isOnline, setIsOnline] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Refs
  const sentenceBufferRef = useRef<SentenceBuffer | null>(null);
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);

  // Handle sentence complete - send to API
  const handleSentenceComplete = useCallback(async (sentence: BufferedSentence) => {
    if (!sessionId || !enableRealTimeSOAP) return;

    // Add to transcript
    setTranscript(prev => [...prev, {
      speaker: sentence.speaker,
      text: sentence.text,
      timestamp: sentence.startTimestamp,
    }]);

    // Format text with speaker prefix
    const formattedText = `${sentence.speaker}: ${sentence.text}`;

    // Call callback
    onSentenceSent?.(sentence);

    // Send to API
    setPendingChunks(prev => prev + 1);

    try {
      const result = await realtimeApi.processStreamChunk({
        sessionId,
        text: formattedText,
      });

      if (result) {
        // Accumulate SOAP data
        setAccumulatedSOAP(prev => ({
          subjective: [...prev.subjective, ...result.delta_subjective],
          objective: [...prev.objective, ...result.delta_objective],
          plan: [...prev.plan, ...result.delta_plan],
        }));

        // Call callback
        onDeltaReceived?.(result);
      }
    } catch (err) {
      console.error('[useRealtimeConsultation] Error processing chunk:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to process chunk';
      setError(errorMsg);
      onError?.(err instanceof Error ? err : new Error(errorMsg));
    } finally {
      setPendingChunks(prev => Math.max(0, prev - 1));
    }
  }, [sessionId, enableRealTimeSOAP, onSentenceSent, onDeltaReceived, onError]);

  // Initialize sentence buffer
  useEffect(() => {
    sentenceBufferRef.current = new SentenceBuffer(handleSentenceComplete, {
      minWordsBeforeFlush,
      maxWordsBeforeForceFlush,
    });

    return () => {
      sentenceBufferRef.current?.clear();
    };
  }, [handleSentenceComplete, minWordsBeforeFlush, maxWordsBeforeForceFlush]);

  // Monitor online status
  useEffect(() => {
    const checkOnlineStatus = () => {
      const status = realtimeApi.getQueueStatus();
      setIsOnline(status.isOnline);
    };

    // Check initially
    checkOnlineStatus();

    // Set up interval to check
    const interval = setInterval(checkOnlineStatus, 5000);

    // Listen to browser events
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      clearInterval(interval);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Set up subscription for real-time updates (optional)
  useEffect(() => {
    if (!sessionId || !enableRealTimeSOAP) return;

    subscriptionRef.current = realtimeApi.subscribeToUpdates(
      sessionId,
      (data) => {
        // Handle updates from other devices
        setAccumulatedSOAP(prev => ({
          subjective: [...prev.subjective, ...data.delta_subjective],
          objective: [...prev.objective, ...data.delta_objective],
          plan: [...prev.plan, ...data.delta_plan],
        }));
        onDeltaReceived?.(data);
      },
      (err) => {
        console.error('[useRealtimeConsultation] Subscription error:', err);
        setError(err.message);
        onError?.(err);
      }
    );

    return () => {
      subscriptionRef.current?.unsubscribe();
    };
  }, [sessionId, enableRealTimeSOAP, onDeltaReceived, onError]);

  // Actions
  const addTranscriptSegment = useCallback((text: string, speaker: string, isFinal: boolean) => {
    if (isPaused) return;

    setIsStreaming(true);

    if (!isFinal) {
      // Update partial transcript
      setCurrentPartial(text);
    } else {
      // Clear partial and add to buffer
      setCurrentPartial('');

      const segment: TranscriptSegment = {
        text,
        speaker,
        timestamp: Date.now(),
        isFinal: true,
      };

      sentenceBufferRef.current?.addSegment(segment);
    }
  }, [isPaused]);

  const pause = useCallback(() => {
    setIsPaused(true);
    setIsStreaming(false);
  }, []);

  const resume = useCallback(() => {
    setIsPaused(false);
  }, []);

  const flushBuffer = useCallback(() => {
    sentenceBufferRef.current?.flush();
  }, []);

  const finish = useCallback(async (): Promise<FinalResult> => {
    // Flush any remaining buffer
    sentenceBufferRef.current?.flush();
    setIsStreaming(false);

    // Wait a bit for pending chunks to complete
    if (pendingChunks > 0) {
      await new Promise(resolve => setTimeout(resolve, 2000));
    }

    // Call finish session
    const result = await realtimeApi.finishSession({ sessionId });
    return result;
  }, [sessionId, pendingChunks]);

  const reset = useCallback(() => {
    sentenceBufferRef.current?.clear();
    subscriptionRef.current?.unsubscribe();
    realtimeApi.clearQueueForSession(sessionId);

    setIsStreaming(false);
    setIsPaused(false);
    setTranscript([]);
    setCurrentPartial('');
    setAccumulatedSOAP(DEFAULT_SOAP);
    setPendingChunks(0);
    setError(null);
  }, [sessionId]);

  // Build state object
  const state: RealtimeConsultationState = {
    isStreaming,
    isPaused,
    transcript,
    currentPartial,
    accumulatedSOAP,
    pendingChunks,
    isOnline,
    error,
  };

  // Build actions object
  const actions: RealtimeConsultationActions = {
    addTranscriptSegment,
    pause,
    resume,
    finish,
    reset,
    flushBuffer,
  };

  return [state, actions];
}

// Helper function: Format SOAP for display
export function formatSOAPPreview(soap: AccumulatedSOAP): string {
  const lines: string[] = [];

  if (soap.subjective.length > 0) {
    lines.push('【SUBJECTIVE】');
    soap.subjective.forEach(item => {
      lines.push(`  • ${item}`);
    });
  }

  if (soap.objective.length > 0) {
    if (lines.length > 0) lines.push('');
    lines.push('【OBJECTIVE】');
    soap.objective.forEach(item => {
      lines.push(`  • ${item}`);
    });
  }

  if (soap.plan.length > 0) {
    if (lines.length > 0) lines.push('');
    lines.push('【PLAN】');
    soap.plan.forEach(item => {
      lines.push(`  • ${item}`);
    });
  }

  if (lines.length === 0) {
    return 'Listening for consultation content...';
  }

  return lines.join('\n');
}

// Helper function: Get SOAP counts
export function getSOAPCounts(soap: AccumulatedSOAP): { s: number; o: number; p: number; total: number } {
  return {
    s: soap.subjective.length,
    o: soap.objective.length,
    p: soap.plan.length,
    total: soap.subjective.length + soap.objective.length + soap.plan.length,
  };
}

export default useRealtimeConsultation;
